
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { SubmitDocumentForm } from '@/components/admin/documents/SubmitDocumentForm';
import { DocumentsTable } from '@/components/admin/documents/DocumentsTable';
import { DocumentDetailView } from '@/components/admin/documents/DocumentDetailView';
import { DocumentRejectionDialog } from '@/components/admin/documents/DocumentRejectionDialog';
import { DocumentSubmission, useDocumentSubmissions } from '@/hooks/useDocumentSubmissions';
import { FileCheck, FilePlus, FileText } from 'lucide-react';

export default function Documents() {
  const [activeTab, setActiveTab] = useState('manage');
  const [selectedDocument, setSelectedDocument] = useState<DocumentSubmission | null>(null);
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
  const { updateDocumentStatus, isUpdating, fetchDocuments } = useDocumentSubmissions();
  
  const { refetch } = useQuery({
    queryKey: ['documents'],
    queryFn: fetchDocuments,
    enabled: true
  });

  const handleViewDocument = (document: DocumentSubmission) => {
    setSelectedDocument(document);
  };

  const handleBackToList = () => {
    setSelectedDocument(null);
    refetch();
  };

  const handleApproveDocument = (documentId: string) => {
    updateDocumentStatus({ 
      id: documentId, 
      status: 'approved' 
    }, {
      onSuccess: () => {
        refetch();
        if (selectedDocument && selectedDocument.id === documentId) {
          // Update the selected document with the new status
          setSelectedDocument({
            ...selectedDocument,
            status: 'approved',
            approved_at: new Date().toISOString(),
            approved_by: 'current_user' // This will be replaced by the actual user ID in the backend
          });
        }
      }
    });
  };

  const handleOpenRejectionDialog = () => {
    setIsRejectionDialogOpen(true);
  };

  const handleRejectDocument = (reason: string) => {
    if (!selectedDocument) return;
    
    updateDocumentStatus({ 
      id: selectedDocument.id, 
      status: 'rejected',
      reason 
    }, {
      onSuccess: () => {
        setIsRejectionDialogOpen(false);
        refetch();
        // Update the selected document with the new status
        setSelectedDocument({
          ...selectedDocument,
          status: 'rejected',
          rejection_reason: reason
        });
      }
    });
  };

  const handleSubmitSuccess = () => {
    refetch();
    setActiveTab('manage');
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">
            Upload, manage, and review documents for compliance and reporting
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="manage">
              <FileText className="h-4 w-4 mr-2" />
              Manage Documents
            </TabsTrigger>
            <TabsTrigger value="submit">
              <FilePlus className="h-4 w-4 mr-2" />
              Submit Document
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manage" className="space-y-4">
            {selectedDocument ? (
              <DocumentDetailView 
                document={selectedDocument}
                onBack={handleBackToList}
                onApprove={handleApproveDocument}
                onReject={handleOpenRejectionDialog}
              />
            ) : (
              <DocumentsTable 
                onViewDocument={handleViewDocument}
                onReviewDocument={handleViewDocument}
              />
            )}
          </TabsContent>

          <TabsContent value="submit">
            <SubmitDocumentForm onSuccess={handleSubmitSuccess} />
          </TabsContent>
        </Tabs>

        <DocumentRejectionDialog 
          isOpen={isRejectionDialogOpen}
          onClose={() => setIsRejectionDialogOpen(false)}
          onReject={handleRejectDocument}
          isLoading={isUpdating}
        />
      </div>
    </AdminLayout>
  );
}
